import { useState, type ReactNode } from 'react';
import {
  Callout,
  Chip,
  EmailInput,
  Empty,
  Field,
  Loading,
  Modal,
  Panel,
  Select,
  TableScroll,
  TextInput,
  explainFailure,
  isApiClientError,
  kitText,
  useLoad,
} from '@affdice/portal-kit';

import { PlatformApi } from '../../../client.ts';
import { t } from '../../../messages.ts';
import { Failure } from '../../shared.tsx';
import { LoginAsButton } from '../../login-as/LoginAs.tsx';
import { roleLabel } from '../../platform/roles-permissions-model.ts';
import {
  ACTION_WORDS,
  addBody,
  addProblems,
  brandSummary,
  deleteBody,
  deleteFailureKey,
  deleteProblems,
  displayName,
  editBody,
  needsSuccessor,
  rowActions,
  signInState,
  successorCandidates,
  teamCapabilities,
  type TeamAction,
  type TeamCapabilities,
  type TeamData,
  type TeamMember,
} from './team-model.ts';

/**
 * Operator → Team (Rule Book 3.12). The operator's own staff and brand-scoped
 * staff, with what the viewer's platform permissions let them do to each:
 * add, edit, limit to brands, disable and enable ("manage operator team");
 * reset the authenticator, lock and unlock ("reset MFA and lock"). Every
 * change asks for a reason, which the operator is shown.
 *
 * Click-to-load like the other cross-tenant panels: opening it is a logged
 * read (3.9c). No password is ever typed or shown here.
 */
export function OperatorTeamPanel({ api, operatorId, shown, onShow }: {
  api: PlatformApi;
  operatorId: string;
  shown: boolean;
  onShow: () => void;
}) {
  const [version, setVersion] = useState(0);
  const [adding, setAdding] = useState(false);
  const [acting, setActing] = useState<{ member: TeamMember; action: TeamAction } | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // Anyone on the platform may read their own permissions; a failure here
  // leaves the panel read-only rather than offering buttons that may be refused.
  const caps = useLoad(() => api.myCapabilities().then(teamCapabilities), []);
  const loaded = useLoad(
    () => (shown ? api.operatorTeam(operatorId) : Promise.resolve(null)),
    [operatorId, shown, version],
  );

  if (!shown) {
    return (
      <Panel title={t('ops.team.title')}>
        <Callout>{t('ops.team.crossTenant')}</Callout>
        <div className="form-actions">
          <button className="primary" onClick={onShow}>{t('ops.team.view')}</button>
        </div>
      </Panel>
    );
  }
  if (loaded.state === 'error') {
    return (
      <Panel title={t('ops.team.title')}>
        {isApiClientError(loaded.error) && loaded.error.status === 403
          ? <Callout tone="caution">{t('ops.team.forbidden')}</Callout>
          : <Failure error={loaded.error} />}
      </Panel>
    );
  }
  if (loaded.state === 'loading' || !loaded.value || caps.state === 'loading') {
    return <Panel title={t('ops.team.title')}><Loading rows={3} /></Panel>;
  }

  const data = loaded.value;
  const can: TeamCapabilities = caps.state === 'ready'
    ? caps.value
    : { view: true, manage: false, credentials: false, delete: false };
  const refresh = () => setVersion((v) => v + 1);

  return (
    <Panel
      title={t('ops.team.titleFor', { operator: data.operator })}
      aside={
        <span className="row">
          <span className="count count-badge">{data.items.length}</span>
          {can.manage ? (
            <button type="button" className="primary" onClick={() => { setDone(null); setAdding(true); }}>
              {t('ops.team.add')}
            </button>
          ) : null}
        </span>
      }
    >
      {done ? <Callout fold={false}>{done}</Callout> : null}
      {!can.manage && !can.credentials ? <Callout>{t('ops.team.readOnly')}</Callout> : null}

      {data.items.length === 0 ? (
        <Empty>{t('ops.team.empty')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <th>{t('ops.team.colPerson')}</th>
                <th>{t('ops.team.colRole')}</th>
                <th>{t('ops.team.colBrands')}</th>
                <th>{t('ops.team.colSignIn')}</th>
                <th className="col-action"><span className="sr-only">{t('ops.team.colActions')}</span></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((m) => (
                <MemberRow
                  key={m.id}
                  api={api}
                  operatorId={operatorId}
                  member={m}
                  team={data.items}
                  can={can}
                  onAct={(action) => { setDone(null); setActing({ member: m, action }); }}
                />
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}

      {adding ? (
        <AddMember
          api={api}
          operatorId={operatorId}
          data={data}
          onCancel={() => setAdding(false)}
          onDone={(email) => { setAdding(false); setDone(t('ops.team.add.done', { email })); refresh(); }}
        />
      ) : null}

      {acting ? (
        <ActionDialog
          api={api}
          operatorId={operatorId}
          data={data}
          member={acting.member}
          action={acting.action}
          onCancel={() => setActing(null)}
          onDone={(message) => {
            setDone(message ?? t('ops.team.done', { name: displayName(acting.member) }));
            setActing(null);
            refresh();
          }}
        />
      ) : null}
    </Panel>
  );
}

const STATE_CHIP = {
  active: { tone: 'locked', label: 'ops.team.state.active' },
  locked: { tone: 'attention', label: 'ops.team.state.locked' },
  disabled: { tone: 'neutral', label: 'ops.team.state.disabled' },
} as const;

function MemberRow({ api, operatorId, member: m, team, can, onAct }: {
  api: PlatformApi;
  operatorId: string;
  member: TeamMember;
  team: readonly TeamMember[];
  can: TeamCapabilities;
  onAct: (action: TeamAction) => void;
}) {
  const state = signInState(m);
  const chip = STATE_CHIP[state];
  const actions = rowActions(m, can, team);
  const name = displayName(m);
  return (
    <tr className={state === 'disabled' ? 'row-inactive' : undefined}>
      <td>
        <div>
          {name}
          {m.is_owner ? <>{' '}<Chip tone="provisional">{t('ops.team.owner')}</Chip></> : null}
        </div>
        {m.name ? <div className="faint">{m.email}</div> : null}
      </td>
      <td>{m.roles.map(roleLabel).join(', ') || <span className="faint">—</span>}</td>
      <td className={m.brands.length === 0 ? 'faint' : undefined}>{brandSummary(m, t('ops.team.allBrands'))}</td>
      <td>
        <Chip tone={chip.tone}>{t(chip.label)}</Chip>
        <div className="faint">
          {m.mfa_enrolled ? t('ops.team.mfa.on') : t('ops.team.mfa.off')}
          {m.must_change_password ? <> · {t('ops.team.mustChange')}</> : null}
        </div>
      </td>
      <td className="col-action">
        {actions.length > 0 || state === 'active' ? (
          <span className="row" role="group" aria-label={t('ops.team.actionsFor', { name })} style={{ flexWrap: 'nowrap' }}>
            {/* 3.13 — shown only to a platform user holding the capability
                for this kind; a brand-limited member is a brand user. */}
            {state === 'active' ? (
              <LoginAsButton
                api={api}
                operatorId={operatorId}
                kind={m.brands.length > 0 ? 'BRAND_USER' : 'OPERATOR_USER'}
                name={name}
                targetUserId={m.id}
                brands={m.brands}
              />
            ) : null}
            {actions.map((a) => (
              <button
                key={a}
                type="button"
                className={ACTION_WORDS[a].danger ? 'link-button danger' : 'link-button'}
                onClick={() => onAct(a)}
              >
                {t(ACTION_WORDS[a].label)}
              </button>
            ))}
          </span>
        ) : null}
      </td>
    </tr>
  );
}

/** The reason field every write asks for. */
function ReasonField({ value, onChange, missing }: { value: string; onChange: (v: string) => void; missing: boolean }) {
  return (
    <Field
      label={t('ops.team.field.reason')}
      hint={t('ops.team.field.reasonHint')}
      {...(missing ? { error: t('ops.team.field.reasonMissing') } : {})}
    >
      <TextInput value={value} onChange={onChange} invalid={missing} />
    </Field>
  );
}

function BrandChoices({ brands, chosen, onChange }: {
  brands: TeamData['brands'];
  chosen: readonly string[];
  onChange: (next: string[]) => void;
}) {
  if (brands.length === 0) return <p className="faint">{t('ops.team.field.noBrands')}</p>;
  return (
    <>
      {brands.map((b) => (
        <label key={b.id} className="checkbox-stack-row">
          <input
            type="checkbox"
            checked={chosen.includes(b.id)}
            onChange={(e) => onChange(e.target.checked ? [...chosen, b.id] : chosen.filter((x) => x !== b.id))}
          />
          {' '}{b.name}
        </label>
      ))}
    </>
  );
}

function DialogButtons({ busy, danger, disabled = false, label, onCancel, onConfirm }: {
  busy: boolean; danger: boolean; disabled?: boolean; label: string; onCancel: () => void; onConfirm: () => void;
}) {
  return (
    <div className="form-actions">
      <button type="button" className="copy" disabled={busy} onClick={onCancel}>{kitText('action.cancel')}</button>
      <button type="button" className={danger ? 'danger' : 'primary'} disabled={busy || disabled} onClick={onConfirm}>
        {busy ? t('ops.team.working') : label}
      </button>
    </div>
  );
}

function AddMember({ api, operatorId, data, onCancel, onDone }: {
  api: PlatformApi;
  operatorId: string;
  data: TeamData;
  onCancel: () => void;
  onDone: (email: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [brandIds, setBrandIds] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const form = { email, name, roleId, brandIds, reason };
  const problems = tried ? addProblems(form) : [];

  return (
    <Modal title={t('ops.team.add.title', { operator: data.operator })} onClose={() => { if (!busy) onCancel(); }}>
      <div className="form">
        {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
        <Callout fold={false}>{t('ops.team.add.body')}</Callout>
        <div className="form-row">
          <Field
            label={t('ops.team.field.email')}
            {...(problems.includes('email') ? { error: t('ops.team.field.emailInvalid') } : {})}
          >
            <EmailInput value={email} onChange={setEmail} invalid={problems.includes('email')} />
          </Field>
          <Field label={t('ops.team.field.name')} hint={t('ops.team.field.nameHint')}>
            <TextInput value={name} onChange={setName} />
          </Field>
        </div>
        <Field
          label={t('ops.team.field.role')}
          {...(problems.includes('role') ? { error: t('ops.team.field.roleMissing') } : {})}
        >
          <Select
            value={roleId}
            onChange={setRoleId}
            placeholder={t('ops.team.field.rolePick')}
            options={data.roles.map((r) => ({ value: r.id, label: roleLabel(r.code) }))}
          />
        </Field>
        <Field group label={t('ops.team.field.brands')} hint={t('ops.team.field.brandsHint')}>
          <BrandChoices brands={data.brands} chosen={brandIds} onChange={setBrandIds} />
        </Field>
        <ReasonField value={reason} onChange={setReason} missing={problems.includes('reason')} />
        <DialogButtons
          busy={busy}
          danger={false}
          label={t('ops.team.add.submit')}
          onCancel={onCancel}
          onConfirm={() => {
            setTried(true);
            if (addProblems(form).length > 0) return;
            setBusy(true);
            setFailure(null);
            api.addOperatorStaff(operatorId, addBody(form))
              .then((r) => onDone(r.email))
              .catch(setFailure)
              .finally(() => setBusy(false));
          }}
        />
      </div>
    </Modal>
  );
}

type TeamBody = { reason: string; name?: string; role_id?: string; brand_ids?: string[] };

/** The failure as words: a delete's own refusals get plain lines, anything else the server's. */
function failureText(failure: unknown, action: TeamAction): string {
  if (action === 'delete' && isApiClientError(failure)) {
    const key = deleteFailureKey(failure.code);
    if (key) return t(key);
  }
  return explainFailure(failure).message;
}

/** Each action is its own route, named here so a reader sees which one a button reaches. */
function send(api: PlatformApi, operatorId: string, userId: string, action: TeamAction, body: TeamBody): Promise<unknown> {
  switch (action) {
    case 'edit': return api.editOperatorStaff(operatorId, userId, body);
    case 'brands': return api.setOperatorStaffBrands(operatorId, userId, { brand_ids: body.brand_ids ?? [], reason: body.reason });
    case 'disable': return api.disableOperatorStaff(operatorId, userId, body);
    case 'enable': return api.enableOperatorStaff(operatorId, userId, body);
    case 'mfa-reset': return api.resetOperatorStaffMfa(operatorId, userId, body);
    case 'lock': return api.lockOperatorStaff(operatorId, userId, body);
    case 'unlock': return api.unlockOperatorStaff(operatorId, userId, body);
    case 'delete': throw new Error('delete is sent with its own body');
  }
}

function ActionDialog({ api, operatorId, data, member, action, onCancel, onDone }: {
  api: PlatformApi;
  operatorId: string;
  data: TeamData;
  member: TeamMember;
  action: TeamAction;
  onCancel: () => void;
  onDone: (message?: string) => void;
}) {
  const words = ACTION_WORDS[action];
  const name = displayName(member);
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [nothing, setNothing] = useState(false);
  const [draftName, setDraftName] = useState(member.name ?? '');
  const [roleId, setRoleId] = useState(data.roles.find((r) => member.roles.includes(r.code))?.id ?? '');
  const [brandIds, setBrandIds] = useState<string[]>(member.brands.map((b) => b.id));
  const [confirmEmail, setConfirmEmail] = useState('');
  const [successorId, setSuccessorId] = useState('');
  const [tried, setTried] = useState(false);
  const mustPick = action === 'delete' && needsSuccessor(member, data.items);
  const candidates = mustPick ? successorCandidates(member, data.items) : [];
  const blocked = mustPick && candidates.length === 0;
  const delForm = { reason, confirmEmail, successorId };
  const delProblems = action === 'delete' && tried ? deleteProblems(delForm, { email: member.email, needsSuccessor: mustPick }) : [];

  let fields: ReactNode = null;
  if (action === 'edit') {
    fields = (
      <>
        <Field label={t('ops.team.field.name')}>
          <TextInput value={draftName} onChange={(v) => { setDraftName(v); setNothing(false); }} />
        </Field>
        <Field label={t('ops.team.field.role')} {...(member.is_owner ? { hint: t('ops.team.field.ownerRole') } : {})}>
          {member.is_owner ? (
            <p>{roleLabel('OPERATOR_OWNER')}</p>
          ) : (
            <Select
              value={roleId}
              onChange={(v) => { setRoleId(v); setNothing(false); }}
              placeholder={t('ops.team.field.rolePick')}
              options={data.roles.map((r) => ({ value: r.id, label: roleLabel(r.code) }))}
            />
          )}
        </Field>
      </>
    );
  } else if (action === 'brands') {
    fields = (
      <Field group label={t('ops.team.field.brands')} hint={t('ops.team.field.brandsHint')}>
        <BrandChoices brands={data.brands} chosen={brandIds} onChange={setBrandIds} />
      </Field>
    );
  }

  if (action === 'delete') {
    fields = (
      <>
        <Callout tone="caution" fold={false}>{t('ops.team.delete.nameKept')}</Callout>
        {mustPick ? (
          blocked ? (
            <Callout tone="caution" fold={false}>{t('ops.team.delete.none')}</Callout>
          ) : (
            <Field
              label={t('ops.team.delete.successor')}
              hint={t('ops.team.delete.successorHelp', { name })}
              {...(delProblems.includes('successor') ? { error: t('ops.team.delete.successorMissing') } : {})}
            >
              <Select
                value={successorId}
                onChange={setSuccessorId}
                placeholder={t('ops.team.delete.successorPick')}
                options={candidates.map((c) => ({ value: c.id, label: `${displayName(c)}${c.name ? ` (${c.email})` : ''}` }))}
              />
            </Field>
          )
        ) : null}
        <Field
          label={t('ops.team.delete.confirm', { email: member.email })}
          {...(delProblems.includes('confirm') ? { error: t('ops.team.delete.mismatch') } : {})}
        >
          <EmailInput value={confirmEmail} onChange={setConfirmEmail} invalid={delProblems.includes('confirm')} />
        </Field>
      </>
    );
  }

  const submit = () => {
    if (action === 'delete') {
      setTried(true);
      if (blocked || deleteProblems(delForm, { email: member.email, needsSuccessor: mustPick }).length > 0) return;
      const successor = candidates.find((c) => c.id === successorId);
      setBusy(true);
      setFailure(null);
      api.deleteOperatorStaff(operatorId, member.id, deleteBody(delForm))
        .then(() => onDone(
          mustPick && successor
            ? t('ops.team.delete.doneOwner', { name, successor: displayName(successor) })
            : t('ops.team.delete.done', { name }),
        ))
        .catch(setFailure)
        .finally(() => setBusy(false));
      return;
    }
    if (!reason.trim()) { setMissing(true); return; }
    let body: TeamBody = { reason: reason.trim() };
    if (action === 'edit') {
      const edit = editBody(member, data.roles, { name: draftName, roleId, reason });
      if (!edit) { setNothing(true); return; }
      body = edit;
    } else if (action === 'brands') {
      body = { brand_ids: brandIds, reason: reason.trim() };
    }
    setBusy(true);
    setFailure(null);
    send(api, operatorId, member.id, action, body)
      .then(() => onDone())
      .catch(setFailure)
      .finally(() => setBusy(false));
  };

  return (
    <Modal title={t(words.title, { name })} onClose={() => { if (!busy) onCancel(); }}>
      <div className="form">
        {failure ? <Callout tone="error">{failureText(failure, action)}</Callout> : null}
        {nothing ? <Callout tone="caution">{t('ops.team.nothingChanged')}</Callout> : null}
        <Callout tone={words.danger ? 'caution' : 'note'} fold={false}>{t(words.body)}</Callout>
        {fields}
        <ReasonField value={reason} onChange={(v) => { setReason(v); setMissing(false); }} missing={missing || delProblems.includes('reason')} />
        <DialogButtons
          busy={busy}
          danger={words.danger}
          disabled={blocked}
          label={action === 'edit' || action === 'brands' ? t('ops.team.confirm') : action === 'delete' ? t('ops.team.delete.submit') : t(words.label)}
          onCancel={onCancel}
          onConfirm={submit}
        />
      </div>
    </Modal>
  );
}
